class RunmodeStorefront {
  constructor() {
    this.drawerSelector = '#RunmodeCartDrawer'
    this.root = window.Runmode?.root || '/'
    this.lastTrigger = null
    this.bind()
    this.initializeSections(document)
  }

  bind() {
    document.addEventListener('click', (event) => this.handleClick(event))
    document.addEventListener('change', (event) => this.handleChange(event))
    document.addEventListener('submit', (event) => this.handleSubmit(event))
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeCart()
        this.closeMenu()
        document.querySelectorAll('[data-modal].is-open').forEach((modal) => this.closeModal(modal))
      }
    })
    document.addEventListener('shopify:section:load', (event) => this.initializeSections(event.target))
  }

  initializeSections(scope) {
    scope.querySelectorAll('[data-sticky-source]').forEach((source) => {
      if (source.dataset.observed) return
      source.dataset.observed = 'true'
      const target = document.querySelector('[data-sticky-cart]')
      if (!target) return
      const observer = new IntersectionObserver(([entry]) => {
        target.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0)
      })
      observer.observe(source)
    })
  }

  handleClick(event) {
    const target = event.target.closest('button, a')
    if (!target) return
    if (target.matches('[data-menu-toggle]')) this.toggleMenu(target)
    if (target.matches('.mobile-nav a')) this.closeMenu()
    if (target.matches('[data-cart-open]')) {
      this.lastTrigger = target
      this.openCart()
    }
    if (target.matches('[data-cart-close]')) this.closeCart()
    if (target.matches('[data-cart-quantity]')) this.changeCart(target)
    if (target.matches('[data-quantity-step]')) this.changeQuantity(target)
    if (target.matches('[data-hotspot]')) this.activateHotspot(target)
    if (target.matches('[data-modal-open]')) this.openModal(target.dataset.modalOpen)
    if (target.matches('[data-modal-close]')) this.closeModal(target.closest('[data-modal]'))
    if (target.matches('[data-gallery-thumb]')) this.setGalleryIndex(target.closest('[data-pdp-gallery]'), Number(target.dataset.galleryThumb))
    if (target.matches('[data-gallery-previous]')) this.moveGallery(target.closest('[data-pdp-gallery]'), -1)
    if (target.matches('[data-gallery-next]')) this.moveGallery(target.closest('[data-pdp-gallery]'), 1)
    if (target.matches('[data-gallery-zoom-open]')) this.openGalleryZoom(target.closest('[data-pdp-gallery]'))
    if (target.matches('[data-gallery-zoom-close]')) target.closest('[data-gallery-zoom]')?.close()
  }

  handleChange(event) {
    const input = event.target
    if (!input.matches('[data-variant-option]')) return
    const picker = input.closest('[data-variant-picker]')
    if (!picker) return
    const options = Array.from(picker.querySelectorAll('[data-variant-option]:checked')).map((option) => option.value)
    const variants = JSON.parse(picker.querySelector('[data-variant-json]').textContent)
    const variant = variants.find((item) => item.options.every((option, index) => option === options[index]))
    const container = picker.closest('.runmode-section')
    if (!container || !variant) return
    container.querySelectorAll('input[name="id"]').forEach((field) => { field.value = variant.id })
    container.querySelectorAll('[data-product-price]').forEach((price) => { price.textContent = this.formatMoney(variant.price) })
    container.querySelectorAll('[data-product-submit]').forEach((button) => {
      button.disabled = !variant.available
      button.setAttribute('aria-disabled', String(!variant.available))
      button.querySelector('[data-submit-label]').textContent = variant.available ? button.dataset.availableLabel : button.dataset.soldLabel
    })
    const selectedLabel = picker.querySelector(`[data-option-selected="${input.dataset.optionPosition}"]`)
    if (selectedLabel) selectedLabel.textContent = input.dataset.optionLabel || input.value
    this.updateVariantImage(picker, variant)
    const url = new URL(window.location.href)
    url.searchParams.set('variant', variant.id)
    window.history.replaceState({}, '', url)
  }

  updateVariantImage(picker, variant) {
    const section = picker.closest('.runmode-section')
    const image = section?.querySelector('[data-variant-image-container] img')
    const variantImage = variant.featured_image
    const source = variantImage?.src || variant.featured_media?.preview_image?.src
    if (!image || !source || image.src === source) return
    image.classList.add('is-changing')
    const preload = new Image()
    preload.onload = () => {
      image.removeAttribute('srcset')
      image.removeAttribute('sizes')
      image.src = source
      if (variantImage?.width) image.width = variantImage.width
      if (variantImage?.height) image.height = variantImage.height
      image.alt = variant.title
      image.classList.remove('is-changing')
      this.setGalleryIndex(section?.querySelector('[data-pdp-gallery]'), 0)
    }
    preload.onerror = () => image.classList.remove('is-changing')
    preload.src = source
  }

  async handleSubmit(event) {
    const form = event.target
    if (!form.matches('[data-runmode-product-form]')) return
    event.preventDefault()
    const button = form.querySelector('[data-product-submit]')
    const message = form.querySelector('[data-product-message]')
    button.disabled = true
    message.textContent = ''
    try {
      const response = await fetch(`${this.root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) })
      if (!response.ok) throw new Error()
      await this.refreshCart()
      this.openCart()
    } catch {
      message.textContent = form.dataset.errorMessage
      message.classList.add('is-error')
    } finally {
      button.disabled = false
    }
  }

  toggleMenu(button) {
    const menu = document.querySelector('#RunmodeMobileMenu')
    const open = !menu.classList.contains('is-open')
    menu.classList.toggle('is-open', open)
    button.setAttribute('aria-expanded', String(open))
  }

  closeMenu() {
    const menu = document.querySelector('#RunmodeMobileMenu')
    const button = document.querySelector('[data-menu-toggle]')
    menu?.classList.remove('is-open')
    button?.setAttribute('aria-expanded', 'false')
  }

  openCart() {
    const drawer = document.querySelector(this.drawerSelector)
    const scrim = document.querySelector('.runmode-cart-scrim')
    drawer?.classList.add('is-open')
    drawer?.setAttribute('aria-hidden', 'false')
    scrim?.classList.add('is-open')
    document.body.style.overflow = 'hidden'
    drawer?.querySelector('[data-cart-close]')?.focus()
  }

  closeCart() {
    const drawer = document.querySelector(this.drawerSelector)
    const scrim = document.querySelector('.runmode-cart-scrim')
    if (!drawer?.classList.contains('is-open')) return
    drawer.classList.remove('is-open')
    drawer.setAttribute('aria-hidden', 'true')
    scrim?.classList.remove('is-open')
    document.body.style.overflow = ''
    this.lastTrigger?.focus()
  }

  async changeCart(button) {
    button.disabled = true
    await fetch(`${this.root}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line: Number(button.dataset.cartQuantity), quantity: Number(button.dataset.quantity) }) })
    await this.refreshCart(true)
  }

  changeQuantity(button) {
    const input = button.parentElement.querySelector('input[name="quantity"]')
    if (!input) return
    input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + Number(button.dataset.quantityStep))
  }

  async refreshCart(keepOpen = false) {
    const response = await fetch(`${this.root}?sections=runmode-cart-drawer`)
    const sections = await response.json()
    const parser = new DOMParser()
    const next = parser.parseFromString(sections['runmode-cart-drawer'], 'text/html')
    const currentSection = document.querySelector('#shopify-section-runmode-cart-drawer')
    currentSection.innerHTML = next.querySelector('#shopify-section-runmode-cart-drawer').innerHTML
    const cartResponse = await fetch(`${this.root}cart.js`)
    const cart = await cartResponse.json()
    document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count || '' })
    if (keepOpen) this.openCart()
  }

  activateHotspot(button) {
    const section = button.closest('[data-hotspot-section]')
    const index = button.dataset.hotspot
    section.querySelectorAll('[data-hotspot]').forEach((item) => {
      const active = item.dataset.hotspot === index
      item.classList.toggle('is-active', active)
      item.setAttribute('aria-pressed', String(active))
    })
    section.querySelectorAll('[data-hotspot-detail]').forEach((detail) => { detail.hidden = detail.dataset.hotspotDetail !== index })
  }

  setGalleryIndex(gallery, index) {
    if (!gallery) return
    const slides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'))
    if (!slides.length) return
    const selectedIndex = ((index % slides.length) + slides.length) % slides.length
    slides.forEach((slide, slideIndex) => { slide.hidden = slideIndex !== selectedIndex })
    gallery.querySelectorAll('[data-gallery-thumb]').forEach((thumb, thumbIndex) => {
      thumb.setAttribute('aria-current', String(thumbIndex === selectedIndex))
    })
    gallery.dataset.galleryIndex = String(selectedIndex)
  }

  moveGallery(gallery, offset) {
    if (!gallery) return
    this.setGalleryIndex(gallery, Number(gallery.dataset.galleryIndex || 0) + offset)
  }

  openGalleryZoom(gallery) {
    if (!gallery) return
    const activeSlide = Array.from(gallery.querySelectorAll('[data-gallery-slide]')).find((slide) => !slide.hidden)
    const source = activeSlide?.querySelector('img')
    const dialog = gallery.querySelector('[data-gallery-zoom]')
    const image = dialog?.querySelector('[data-gallery-zoom-image]')
    if (!source || !dialog || !image) return
    image.src = source.currentSrc || source.src
    image.alt = source.alt
    dialog.showModal()
  }

  openModal(id) {
    const modal = document.getElementById(id)
    if (!modal) return
    modal.classList.add('is-open')
    modal.hidden = false
    modal.querySelector('[data-modal-close]')?.focus()
  }

  closeModal(modal) {
    if (!modal) return
    modal.classList.remove('is-open')
    modal.hidden = true
  }

  formatMoney(cents) {
    const format = window.Runmode?.moneyFormat || '${{amount}}'
    const value = (Number(cents) / 100).toFixed(2)
    return format.replace('{{amount}}', value).replace('{{ amount }}', value)
  }
}

new RunmodeStorefront()
